import { useCallback, useEffect, useState } from 'react';
import { useUser } from '../../context/UserContext';
import { useUrlParam } from '../../hooks/useUrlParam';
import { deleteNoteTemplate, fetchNoteTemplates, saveNoteTemplate, type NoteTemplate } from '../../lib/noteTemplates';
import { Button, Card, Input, useToast } from '../ui';
import { when } from './promptStyles';

const faint: React.CSSProperties = { color: 'var(--text-faint)' };

/** Draft being edited: an existing template (`id`) or a new one (`id: null`). */
type Draft = { id: string | null; name: string; body: string };

/**
 * Agents → Note templates: canned notes for the "Send to spec queue" drawer. The name is what the drawer's
 * dropdown lists; picking one copies the body into the note, where it can still be edited before sending.
 * The template open for editing is ?template=<id>; a new one stays local until it is saved.
 */
export default function NoteTemplatesView() {
  const { user } = useUser();
  const toast = useToast();
  const [templates, setTemplates] = useState<NoteTemplate[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [templateParam, setTemplateParam] = useUrlParam('template');

  const load = useCallback(async () => {
    try {
      setTemplates(await fetchNoteTemplates());
      setLoadError(null);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Could not load the templates');
      setTemplates([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // The URL opens and closes an existing template; a new draft has no id to put there.
  useEffect(() => {
    if (!templates) return;
    if (!templateParam) {
      setDraft((d) => (d?.id ? null : d));
      return;
    }
    const t = templates.find((x) => x.id === templateParam);
    setDraft((d) => (d?.id === templateParam ? d : t ? { id: t.id, name: t.name, body: t.body } : null));
  }, [templateParam, templates]);

  const open = (t: NoteTemplate) => {
    setConfirmDelete(false);
    setTemplateParam(t.id);
  };
  const startNew = () => {
    setConfirmDelete(false);
    if (templateParam) setTemplateParam(null);
    setDraft({ id: null, name: '', body: '' });
  };
  const close = () => {
    setDraft(null);
    setConfirmDelete(false);
    if (templateParam) setTemplateParam(null);
  };

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const saved = await saveNoteTemplate(draft.id, draft, user?.id ?? null);
      await load();
      setDraft({ id: saved.id, name: saved.name, body: saved.body });
      setTemplateParam(saved.id);
      toast.success(draft.id ? 'Template saved' : 'Template created', saved.name);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not save the template');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!draft?.id) return;
    setSaving(true);
    try {
      await deleteNoteTemplate(draft.id);
      toast.success('Template deleted', draft.name);
      close();
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not delete the template');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
      <Card
        eyebrow="Send to spec queue"
        title={templates ? `${templates.length} note template${templates.length === 1 ? '' : 's'}` : 'Note templates'}
        actions={
          <Button size="sm" icon="plus" onClick={startNew} disabled={saving}>
            New template
          </Button>
        }
      >
        <div style={{ ...faint, fontSize: 'var(--text-sm)', marginBottom: 'var(--space-3)' }}>
          Canned notes for the spec queue drawer. Picking one there loads it into the note, where you can change it before sending.
        </div>
        {loadError ? (
          <div style={{ color: 'var(--status-down)', fontSize: 'var(--text-sm)' }}>{loadError}</div>
        ) : !templates ? (
          <div style={{ ...faint, fontSize: 'var(--text-sm)' }}>Loading…</div>
        ) : templates.length === 0 ? (
          <div style={{ ...faint, fontSize: 'var(--text-sm)' }}>No templates yet.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {templates.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => open(t)}
                className="hover-wash"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '10px',
                  margin: '0 -10px',
                  border: 0,
                  borderRadius: 'var(--radius-sm)',
                  background: draft?.id === t.id ? 'var(--surface-hover, var(--border-hairline))' : 'transparent',
                  color: 'inherit',
                  font: 'inherit',
                  textAlign: 'left',
                  cursor: 'pointer',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-body)', fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'] }}>{t.name}</div>
                  <div style={{ ...faint, fontSize: 'var(--text-xs)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {t.body.trim().split('\n')[0] || 'Empty note'}
                  </div>
                </div>
                <span className="t-num" style={{ ...faint, fontSize: 'var(--text-xs)', whiteSpace: 'nowrap' }}>
                  {when(t.updated_at)}
                </span>
              </button>
            ))}
          </div>
        )}
      </Card>

      {draft ? (
        <Card eyebrow={draft.id ? 'Edit template' : 'New template'} title={draft.name.trim() || 'Untitled template'}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <Input
              label="Name · shown in the drawer's dropdown"
              value={draft.name}
              onChange={(e) => setDraft((d) => (d ? { ...d, name: e.target.value } : d))}
              placeholder="e.g. Fix a broken contact form"
              disabled={saving}
            />
            <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <span className="t-eyebrow">Note</span>
              <textarea
                className="ck-textarea"
                rows={10}
                value={draft.body}
                onChange={(e) => setDraft((d) => (d ? { ...d, body: e.target.value } : d))}
                placeholder="The note that loads into the spec queue drawer"
                disabled={saving}
              />
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <Button onClick={save} disabled={saving || !draft.name.trim()}>
                {saving ? 'Saving…' : draft.id ? 'Save changes' : 'Create'}
              </Button>
              <Button variant="ghost" onClick={close} disabled={saving}>
                Close
              </Button>
              {draft.id ? (
                <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: '6px' }}>
                  {confirmDelete ? (
                    <>
                      <Button variant="danger" onClick={remove} disabled={saving}>
                        Delete
                      </Button>
                      <Button variant="ghost" onClick={() => setConfirmDelete(false)} disabled={saving}>
                        Keep
                      </Button>
                    </>
                  ) : (
                    <Button variant="ghost" onClick={() => setConfirmDelete(true)} disabled={saving}>
                      Delete template
                    </Button>
                  )}
                </span>
              ) : null}
            </div>
          </div>
        </Card>
      ) : null}
    </div>
  );
}
